import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { StarIcon } from "@heroicons/react/24/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPen } from "@fortawesome/free-solid-svg-icons";
import { useAppSelector } from "../../app/hooks";
import { useScrollToSection } from "../../app/useScrollToSection";
import NoDataTableBody from "../../components/NoDataTableBody";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import Select from "../../components/input/Select";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import { SimpleOption } from "../../components/input/SimpleListbox";
import {
	useGetTutorialsQuery,
	useGetCategoriesQuery,
	useGetUserActivityQuery,
	TutorialViewModel,
	TutorialSessionViewModel,
	TutorialAssignmentViewModel,
} from "./lessonsApiSlice";

interface LessonData extends TutorialViewModel {
	session: TutorialSessionViewModel | undefined;
	assignment: TutorialAssignmentViewModel | undefined;
	recommendation: number | undefined;
	status: LessonStatus | undefined;
}

export enum LessonStatus {
	NotStarted = "Not started",
	InProgress = "In progress",
	Complete = "Complete",
}

const isLessonComplete = (
	lesson: TutorialViewModel,
	session: TutorialSessionViewModel,
	ratio: number
) => (session.timeSpentSeconds / lesson.durationSeconds) * 100 >= ratio;

export default function StudentLessons() {
	const config = useAppSelector((state) => state.site.config);

	useScrollToSection();

	const [view, setView] = useState<string>("table");
	const [statusFilter, setStatusFilter] = useState("assigned");
	const [categoryOptions, setCategoryOptions] = useState<SimpleOption[]>([]);
	const [categoryFilter, setCategoryFilter] = useState("");
	const [difficultyFilter, setDifficultyFilter] = useState("");

	const [lessonData, setLessonData] = useState<LessonData[]>([]);
	const [lessonHistory, setLessonHistory] = useState<LessonData[]>([]);

	const tutsResult = useGetTutorialsQuery();
	const tutorials = tutsResult.data;

	const catsResult = useGetCategoriesQuery();
	const categories = catsResult.data;

	const actiResult = useGetUserActivityQuery();
	const activity = actiResult.data;

	const isFetching = tutsResult.isFetching || catsResult.isFetching || actiResult.isFetching;
	const isError = tutsResult.isError || catsResult.isError || actiResult.isError;
	const isSuccess = tutsResult.isSuccess && catsResult.isSuccess && actiResult.isSuccess;

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (location.state != null) {
			setStatusFilter(location.state.statusFilter);
		}
		navigate(location, { replace: true });
	}, []);

	useEffect(() => {
		let options: SimpleOption[] = [];
		if (categories) {
			options = [...categories]
				.sort((a, b) => a.order - b.order)
				.map((c) => ({
					value: c.categoryId.toString() ?? "",
					label: c.name,
				}));
			setCategoryFilter(options[0].value.toString());
		}
		setCategoryOptions(options);
	}, [categories]);

	useEffect(() => {
		if (!tutorials || !activity || !config) return setLessonData([]);
		const lessons: LessonData[] = tutorials.map((t) => {
			const _session = activity.sessions.find((s) => s.tutorialId == t.tutorialId);
			const _assignment = activity.assignments.find((a) => a.tutorialId == t.tutorialId);
			const _recommendation = activity.recommendations.find((r) => r == t.tutorialId);

			let _status = undefined;
			if (_session) {
				if (isLessonComplete(t, _session, config.tutorialCompletedTimeRatio)) {
					_status = LessonStatus.Complete;
				} else if (_session.timeStarted) {
					_status = LessonStatus.InProgress;
				} else if (_assignment || _recommendation) {
					_status = LessonStatus.NotStarted;
				}
			} else if (_recommendation || _assignment) {
				_status = LessonStatus.NotStarted;
			}

			const x: LessonData = {
				...t,
				session: _session,
				assignment: _assignment,
				recommendation: _recommendation,
				status: _status,
			};
			return x;
		});
		setLessonData(lessons);
		setLessonHistory(lessons.filter((l) => l.status != undefined));
	}, [tutorials, activity]);

	return (
		<>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Lessons</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "table", label: "Tables" },
						{ value: "raw", label: "Raw Data" },
					]}
				/>
			</div>
			<p className="mt-4">
				Welcome to the Lessons page! From this page, you can view your Lesson Activity,
				which includes lessons assigned by your teacher or recommended by {config?.siteName}
				. Additional lessons, listed by category, are available in the Lesson Library to
				help you improve your writing skills. Click on the drop-down menus to view all
				options. All lessons have audio, so get your headphones ready.
			</p>
			{view == "table" && (
				<>
					<section className="mt-8">
						<h2>My Lesson Activity</h2>
						{isFetching && <p>Fetching lesson activity...</p>}
						{isError && <p>Error fetching lesson activity...</p>}
						{isSuccess && (
							<>
								<div className="flex justify-between mt-4">
									<Select
										value={statusFilter}
										setValue={(v) => setStatusFilter(v as string)}
										label="Status"
										simpleListBoxId="statusFilter"
										options={[
											{ value: "all", label: "All" },
											{
												value: "assigned",
												label: "Assigned By Your Teacher",
											},
											{
												value: "recommended",
												label: `Recommended By ${config?.siteName}`,
											},
											{ value: "not started", label: "Not Started" },
											{ value: "in progress", label: "In Progress" },
											{ value: "complete", label: "Complete" },
										]}
										simpleListBoxClasses="w-64"
									/>
								</div>
								<LessonTable
									filteredData={lessonHistory.filter((row) => {
										switch (statusFilter) {
											case "complete":
												return row.status == LessonStatus.Complete;
											case "in progress":
												return row.status == LessonStatus.InProgress;
											case "not started":
												return row.status == LessonStatus.NotStarted;
											case "assigned":
												return row.assignment;
											case "recommended":
												return row.recommendation;
										}
										return true;
									})}
									activityTable={true}
									filter={statusFilter}></LessonTable>
							</>
						)}
					</section>

					<section id="LessonLibrary" className="mt-8">
						<h2>Lesson Library</h2>
						{isFetching && <p>Fetching lessons...</p>}
						{isError && <p>Error fetching lessons...</p>}
						{isSuccess && (
							<>
								<div className="flex justify-between mt-4">
									<Select
										value={categoryFilter}
										setValue={(v) => setCategoryFilter(v as string)}
										label="Category"
										simpleListBoxId="categoryFilter"
										options={categoryOptions}
										simpleListBoxClasses="w-64"
									/>
									<Select
										value={difficultyFilter}
										setValue={(v) => setDifficultyFilter(v as string)}
										label="Difficulty Level"
										simpleListBoxId="difficultyFilter"
										options={[
											{ value: "", label: "All Levels" },
											{ value: "Beginner", label: "Beginner" },
											{
												value: "Intermediate",
												label: "Intermediate",
											},
											{ value: "Advanced", label: "Advanced" },
										]}
										simpleListBoxClasses="w-64"
									/>
								</div>
								<LessonTable
									filteredData={lessonData
										.filter(
											(row) => row.categoryId.toString() == categoryFilter
										)
										.filter(
											(row) =>
												difficultyFilter == "" ||
												difficultyFilter == row.difficulty
										)}
									activityTable={false}></LessonTable>
							</>
						)}
					</section>
				</>
			)}
			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Activity" result={actiResult} />
					<RtkqResultViewer heading="Tutorials" result={tutsResult} />
					<RtkqResultViewer heading="Categories" result={catsResult} />
				</>
			)}
		</>
	);
}

interface LessonTableProps {
	filteredData: LessonData[];
	activityTable: boolean;
	filter?: string;
}

function LessonTable({ filteredData, activityTable, filter }: LessonTableProps) {
	const { config } = useAppSelector((state) => state.site);
	const noDataText = () => {
		switch (filter) {
			case "complete":
				return "You have no completed lessons.";
			case "in progress":
				return "You have no lessons in progress.";
			case "not started":
				return "You have no lessons that have not been started.";
			case "assigned":
				return "You have no lessons assigned by your teacher.";
			case "recommended":
				return `You have no lessons recommended by ${config?.siteName}.`;
			default:
				return "You have no lessons in your lesson activity.";
		}
	};

	return (
		<div className="shadow-sm overflow-hidden mt-4">
			<table className="tbl tbl-data">
				<thead>
					<tr>
						<th>Title</th>
						<th>Level</th>
						<th>Duration</th>
						<th></th>
						<th>Status</th>
						<th>Description</th>
					</tr>
				</thead>
				{filteredData.length > 0 ? (
					<tbody>
						{filteredData.map((row) => (
							<tr key={row.tutorialId}>
								<td>
									<Link
										id={`${activityTable ? `activity` : `library`}-${row.tutorialId}`}
										to={`/lessons/${row.codeName}`}>
										{row.name}
									</Link>
								</td>
								<td>{row.difficulty}</td>
								<td>
									{Math.round(row.durationSeconds / 60)}
									&nbsp;minutes
								</td>
								<td className="!px-0">
									{activityTable || row.status != LessonStatus.Complete ? (
										<div className="relative float-right">
											{row.assignment ? (
												<FontAwesomeIcon
													icon={faUserPen}
													data-tooltip-id="assigned-tooltip"
												/>
											) : row.recommendation ? (
												<StarIcon
													className="h-5 w-5"
													data-tooltip-id="recommended-tooltip"></StarIcon>
											) : (
												<></>
											)}
											<Tooltip
												id="assigned-tooltip"
												place="top-start"
												content="Assigned By Your Teacher"
											/>
											<Tooltip
												id="recommended-tooltip"
												place="top-start"
												content={`Recommended By ${config?.siteName}`}
											/>
										</div>
									) : (
										<></>
									)}
								</td>
								<td>
									<span className="whitespace-nowrap">
										<span>{row.status}</span>
										<div className="mt-1 text-gray-500 text-sm">
											{row.session?.timeSpentSeconds != 0 &&
											row.session?.timeSpentSeconds ? (
												<span>
													{Math.round(row.session?.timeSpentSeconds / 60)}{" "}
													min spent{" "}
												</span>
											) : (
												<></>
											)}
										</div>
									</span>
								</td>
								<td>
									<div className="max-w-[1250px] h-12 line-clamp-2 hover:line-clamp-none hover:h-auto ">
										{row.description}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				) : (
					<NoDataTableBody
						columnCount={6}
						message={
							activityTable
								? noDataText()
								: "There are no lessons available in this category."
						}
					/>
				)}
			</table>
		</div>
	);
}
